import React, { useState, useCallback, memo, useMemo } from "react";
import { Search, Filter, X } from "lucide-react";
import { track } from "@amplitude/analytics-browser";

interface SearchAndFilterProps {
  onSearch: (query: string) => void;
  onFilter: (filters: FilterOptions) => void;
  onSort: (sort: string) => void;
}

interface FilterOptions {
  type?: string;
  region?: string;
  context?: string;
  sponsored?: boolean;
}

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  onSearch,
  onFilter,
  onSort,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [currentSort, setCurrentSort] = useState("downloads");

  // Debounced search implementation to prevent too many API calls
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedSearchQuery !== searchQuery) {
        setDebouncedSearchQuery(searchQuery);
        onSearch(searchQuery);

        // Track search event
        if (searchQuery.trim()) {
          track("Story Search", {
            query: searchQuery,
            timestamp: new Date().toISOString(),
          });
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchQuery, onSearch]);

  // Memoized filter handler to prevent recreation on each render
  const handleFilterChange = useCallback(
    (key: keyof FilterOptions, value: any) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters };

        if (!value) {
          delete newFilters[key];
        } else {
          newFilters[key] = value;
        }

        onFilter(newFilters);

        // Track filter change
        track("Apply Story Filter", {
          filter_type: key,
          filter_value: value,
          total_active_filters: Object.keys(newFilters).length,
          all_filters: newFilters,
          timestamp: new Date().toISOString(),
        });

        return newFilters;
      });
    },
    [onFilter]
  );

  // Memoized search handler to prevent recreation on each render
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    // onSearch is called via the debounce effect
  }, []);

  // Memoized sort handler to prevent recreation on each render
  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setCurrentSort(value);
      onSort(value);

      // Track sort change
      track("Change Story Sort", {
        sort_type: value,
        timestamp: new Date().toISOString(),
      });
    },
    [onSort]
  );

  // Clear all filters with a memoized callback
  const clearAllFilters = useCallback(() => {
    setFilters({});
    setSearchQuery("");
    setDebouncedSearchQuery("");
    onFilter({});
    onSearch("");

    // Track clear filters
    track("Clear Story Filters", {
      previous_filters: filters,
      had_search_query: Boolean(searchQuery),
      timestamp: new Date().toISOString(),
    });
  }, [onFilter, onSearch, filters, searchQuery]);

  // Compute this value once per render
  const hasActiveFilters = useMemo(
    () => Object.keys(filters).length > 0 || Boolean(searchQuery),
    [filters, searchQuery]
  );

  // Toggle filter panel visibility with tracking
  const toggleFilterPanel = useCallback(() => {
    setIsFilterOpen((prev) => {
      const newState = !prev;

      // Track filter panel toggle
      track("Toggle Filter Panel", {
        action: newState ? "open" : "close",
        active_filters: Object.keys(filters).length,
        timestamp: new Date().toISOString(),
      });

      return newState;
    });
  }, [filters]);

  return (
    <div className="space-y-4">
      {/* Search and Filter Toggle */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search stories..."
            value={searchQuery}
            className="w-full pl-10 pr-4 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <button
          onClick={toggleFilterPanel}
          className={`px-4 py-2 rounded-md border flex items-center gap-2 ${
            Object.keys(filters).length > 0
              ? "bg-primary text-primary-foreground"
              : "bg-background hover:bg-muted"
          }`}
        >
          <Filter className="h-4 w-4" />
          Filters{" "}
          {Object.keys(filters).length > 0 &&
            `(${Object.keys(filters).length})`}
        </button>
        <select
          className="px-4 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          value={currentSort}
          onChange={handleSortChange}
        >
          <option value="downloads">Most Popular</option>
          <option value="date">Latest</option>
          <option value="title">Title</option>
          <option value="type">Type</option>
        </select>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Active filters:</span>
          <div className="flex flex-wrap gap-2">
            {searchQuery && (
              <span className="bg-muted px-2 py-1 rounded-md border border-destructive">
                Search: {searchQuery}
              </span>
            )}
            {filters.type && (
              <span className="bg-muted px-2 py-1 rounded-md border border-destructive">
                Type: {filters.type}
              </span>
            )}
            {filters.region && (
              <span className="bg-muted px-2 py-1 rounded-md border border-destructive">
                Region: {filters.region}
              </span>
            )}
            {filters.context && (
              <span className="bg-muted px-2 py-1 rounded-md border border-destructive">
                Context: {filters.context}
              </span>
            )}
            {filters.sponsored !== undefined && (
              <span className="bg-muted px-2 py-1 rounded-md border border-destructive">
                {filters.sponsored ? "Sponsored" : "Available for Sponsorship"}
              </span>
            )}
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1 px-2 py-1 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90"
            >
              <X className="h-3 w-3" />
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className="p-4 bg-card border rounded-lg grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Story Type */}
          <div>
            <label className="text-sm font-medium mb-2 block">Story Type</label>
            <select
              className={`w-full px-3 py-2 bg-background border rounded-md ${
                filters.type ? "border-destructive" : ""
              }`}
              value={filters.type || ""}
              onChange={(e) => handleFilterChange("type", e.target.value)}
            >
              <option value="">All Types</option>
              <option value="murder">Murder</option>
              <option value="theft">Theft</option>
              <option value="brain teaser">Brain Teaser</option>
            </select>
          </div>

          {/* Region */}
          <div>
            <label className="text-sm font-medium mb-2 block">Region</label>
            <select
              className={`w-full px-3 py-2 bg-background border rounded-md ${
                filters.region ? "border-destructive" : ""
              }`}
              value={filters.region || ""}
              onChange={(e) => handleFilterChange("region", e.target.value)}
            >
              <option value="">All Regions</option>
              <option value="USA">USA</option>
              <option value="Canada">Canada</option>
              <option value="Australia">Australia</option>
              <option value="Europe">Europe</option>
              <option value="Asia">Asia</option>
              <option value="Africa">Africa</option>
              <option value="South America">South America</option>
            </select>
          </div>

          {/* Context */}
          <div>
            <label className="text-sm font-medium mb-2 block">Context</label>
            <select
              className={`w-full px-3 py-2 bg-background border rounded-md ${
                filters.context ? "border-destructive" : ""
              }`}
              value={filters.context || ""}
              onChange={(e) => handleFilterChange("context", e.target.value)}
            >
              <option value="">All Contexts</option>
              <option value="high tech">High Tech</option>
              <option value="low tech">Low Tech</option>
            </select>
          </div>

          {/* Sponsorship Status */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Sponsorship
            </label>
            <select
              className={`w-full px-3 py-2 bg-background border rounded-md ${
                filters.sponsored !== undefined ? "border-destructive" : ""
              }`}
              value={
                filters.sponsored === undefined
                  ? ""
                  : filters.sponsored.toString()
              }
              onChange={(e) => {
                const value = e.target.value;
                handleFilterChange(
                  "sponsored",
                  value === "" ? undefined : value === "true"
                );
              }}
            >
              <option value="">All Stories</option>
              <option value="true">Sponsored</option>
              <option value="false">Available for Sponsorship</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(SearchAndFilter);
